import React from 'react';

const OptimizationTab = ({ warningMessage, optimizationStep, isOptimizing, showOptimizedRoutes, optimizedRoutes, onStartOptimization, onResetOptimization, onViewRouteDetails }) => (
  <div>
    {warningMessage && (
      <div style={{ padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        <span>{warningMessage}</span>
      </div>
    )}

    <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f3f4f6' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', margin: 0 }}>Route Optimization Engine</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          {showOptimizedRoutes && (
            <button onClick={onResetOptimization} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>🔄 Reset</button>
          )}
          {!showOptimizedRoutes && (
            <button onClick={onStartOptimization} disabled={isOptimizing} style={{ padding: '10px 20px', background: isOptimizing ? '#9ca3af' : '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: isOptimizing ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isOptimizing ? 'Calling API...' : '▶️ Start Optimization'}
            </button>
          )}
        </div>
      </div>

      <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '20px' }}>
        <h4 style={{ marginBottom: '15px', color: '#1f2937' }}>Optimization Process</h4>
        {[
          'Checking vehicle availability and capacity',
          'Analyzing delivery priorities and time windows',
          'Calculating optimal distances via GraphHopper',
          'Distributing workload across available vehicles',
          'Generating optimized routes'
        ].map((text, idx) => {
          const stepNum = idx + 1;
          const isActive = optimizationStep === stepNum;
          const isCompleted = optimizationStep > stepNum || (stepNum === 5 && showOptimizedRoutes);
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '12px', marginBottom: '10px', borderRadius: '8px', background: 'white', opacity: isCompleted || isActive ? 1 : 0.4, boxShadow: isActive ? '0 2px 8px rgba(102,126,234,0.2)' : 'none', borderLeft: isCompleted ? '4px solid #10b981' : isActive ? '4px solid #667eea' : '4px solid transparent' }}>
              <div style={{ width: '35px', height: '35px', background: isCompleted ? '#10b981' : '#667eea', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                {isCompleted ? '✓' : stepNum}
              </div>
              <div style={{ flex: 1, fontSize: '15px', color: '#1f2937' }}>{text}</div>
            </div>
          );
        })}
      </div>

      {showOptimizedRoutes && optimizedRoutes.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ marginBottom: '15px', color: '#1f2937' }}>Optimized Routes (via API)</h4>
          {optimizedRoutes.map((route, idx) => (
            <div key={idx} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '20px', borderRadius: '12px', marginBottom: '15px', boxShadow: '0 4px 12px rgba(102,126,234,0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600 }}>🚚 {route.vehicle.name} - {route.vehicle.driver}</div>
                <span style={{ background: '#d1fae5', color: '#065f46', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 500 }}>Optimized</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.2)', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                <h5 style={{ fontSize: '14px', marginBottom: '10px', opacity: 0.9 }}>Route ({route.deliveries.length} stops)</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  {route.routeSequence.map((location, i) => (
                    <React.Fragment key={i}>
                      <span style={{ background: 'white', color: '#667eea', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500 }}>{location}</span>
                      {i < route.routeSequence.length - 1 && <span>→</span>}
                    </React.Fragment>
                  ))}
                </div>
                <p style={{ marginTop: '10px', fontSize: '13px', opacity: 0.9 }}>Load: {route.totalWeight}kg / {route.vehicle.capacity}kg</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                {[
                  { label: 'Distance', value: `${route.totalDistance} km` },
                  { label: 'Time', value: `${Math.floor(route.totalTime)} hrs ${Math.round((route.totalTime % 1) * 60)} mins` },
                  { label: 'Cost', value: `₹${route.totalCost}` },
                  { label: 'Stops', value: route.deliveries.length }
                ].map((stat, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '5px' }}>{stat.label}</div>
                    <div style={{ fontSize: '18px', fontWeight: 600 }}>{stat.value}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => onViewRouteDetails(route)} style={{ marginTop: '15px', padding: '10px 20px', background: 'white', color: '#667eea', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, width: '100%' }}>
                View Route Details
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default OptimizationTab;
